import { SettingsFormMarkupFactory } from "@shesha-io/reactjs";
import { nanoid } from "nanoid";

// 0.46.0 removed DesignerToolbarSettings. Settings are built through the typed factory the
// framework passes in, following the shape the framework's own components use:
//
//   * one addSearchableTabs wrapper - Common / Data / Appearance are the standard keys - so the
//     properties panel is tabbed rather than one long scroll
//   * ids are never written by hand. The builder assigns component ids; each tab needs its own
//     id (ITabPaneProps.id is required), so generate one and pass it to fbf(tabId) - that is
//     what parents the tab's children, which is why none of them set parentId
//   * inputs go through addSettingsInput / addSettingsInputRow. Note the two spellings:
//     addSettingsInput takes `inputType`, an input nested in a row takes `type`
//   * size: 'small' + styledLabel keep the labels in the designer's own type scale
const commonTabId = nanoid();
const dataTabId = nanoid();
const appearanceTabId = nanoid();

export const settingsForm: SettingsFormMarkupFactory = ({ fbf }) => ({
  components: fbf("root")
    .addSearchableTabs({
      propertyName: "settingsTabs",
      parentId: "root",
      label: "Settings",
      hideLabel: true,
      labelAlign: "right",
      size: "small",
      tabs: [
        {
          key: "common",
          title: "Common",
          id: commonTabId,
          components: fbf(commonTabId)
            .addContextPropertyAutocomplete({
              propertyName: "propertyName",
              label: "Property Name",
              size: "small",
              styledLabel: true,
              validate: { required: true },
            })
            .addSettingsInput({
              inputType: "textField",
              propertyName: "label",
              label: "Label",
              size: "small",
              description: "The label/title displayed at the top of the player. If empty, uses Property name.",
              placeholder: "Call Recording",
              jsSetting: true,
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "switch",
                  propertyName: "hideLabel",
                  label: "Hide Label",
                  size: "small",
                  description: "Hide the component label",
                  jsSetting: true,
                },
                {
                  type: "switch",
                  propertyName: "showDownloadButton",
                  label: "Show Download Button",
                  size: "small",
                  description: "Display the download button below the audio player",
                  jsSetting: true,
                },
              ],
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "switch",
                  propertyName: "showControls",
                  label: "Show Audio Controls",
                  size: "small",
                  description: "Display the audio player controls (play, pause, volume, etc.)",
                  jsSetting: true,
                },
                {
                  type: "switch",
                  propertyName: "compact",
                  label: "Compact Mode",
                  size: "small",
                  description: "Display as a single line with minimal controls (play/pause, time, download)",
                  jsSetting: true,
                },
              ],
            })
            .toJson(),
        },
        {
          key: "data",
          title: "Data",
          id: dataTabId,
          components: fbf(dataTabId)
            .addSettingsInput({
              inputType: "textField",
              propertyName: "url",
              label: "Recording URL",
              size: "small",
              description: "API endpoint URL for downloading recordings. The ID will be appended to this URL.",
              placeholder: "/api/services/Telephony/AWS/CallDetails/DownloadRecording?id=",
              jsSetting: true,
            })
            .addSettingsInput({
              inputType: "textField",
              propertyName: "recordingId",
              label: "Recording ID",
              size: "small",
              description: "The call recording ID. Supports expressions like {{data.id}} to use form context data.",
              placeholder: "{{data.id}}",
              jsSetting: true,
            })
            .addSettingsInput({
              inputType: "textField",
              propertyName: "recordingDate",
              label: "Recording Date (Optional)",
              size: "small",
              description: "The date/time of the recording. Leave empty to automatically extract from recording ID (format: uuid_2025-08-21T11:07:02.327Z). Supports expressions like {{data.createdOn}}.",
              placeholder: "Leave empty for auto-extract",
              jsSetting: true,
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "switch",
                  propertyName: "autoplay",
                  label: "Autoplay",
                  size: "small",
                  description: "Automatically start playing the recording when loaded (Note: Most browsers require user interaction first)",
                  jsSetting: true,
                },
                {
                  type: "switch",
                  propertyName: "loop",
                  label: "Loop",
                  size: "small",
                  description: "Automatically restart the recording when it reaches the end",
                  jsSetting: true,
                },
              ],
            })
            .addSettingsInput({
              inputType: "dropdown",
              propertyName: "preload",
              label: "Preload",
              size: "small",
              description: "Hint to the browser about how much buffering is needed",
              dropdownOptions: [
                { value: "none", label: "None - Don't preload" },
                { value: "metadata", label: "Metadata - Preload only metadata" },
                { value: "auto", label: "Auto - Preload entire file" },
              ],
              jsSetting: true,
            })
            .toJson(),
        },
        {
          key: "appearance",
          title: "Appearance",
          id: appearanceTabId,
          components: fbf(appearanceTabId)
            .addSettingsInputRow({
              inputs: [
                {
                  type: "colorPicker",
                  propertyName: "backgroundColor",
                  label: "Background Color",
                  size: "small",
                  description: "Set the background color of the player card",
                  jsSetting: true,
                },
                {
                  type: "colorPicker",
                  propertyName: "borderColor",
                  label: "Border Color",
                  size: "small",
                  description: "Set the border color of the player card",
                  jsSetting: true,
                },
              ],
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "numberField",
                  propertyName: "borderRadius",
                  label: "Border Radius (px)",
                  size: "small",
                  description: "Set the border radius in pixels (e.g., 8 for rounded corners)",
                  jsSetting: true,
                },
                {
                  type: "numberField",
                  propertyName: "borderWidth",
                  label: "Border Width (px)",
                  size: "small",
                  description: "Set the border width in pixels",
                  jsSetting: true,
                },
                {
                  type: "numberField",
                  propertyName: "padding",
                  label: "Padding (px)",
                  size: "small",
                  description: "Set the internal padding in pixels",
                  jsSetting: true,
                },
              ],
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "textField",
                  propertyName: "width",
                  label: "Width",
                  size: "small",
                  description: "Set the width (e.g., '100%', '500px', '50vw')",
                  placeholder: "100%",
                  jsSetting: true,
                },
                {
                  type: "textField",
                  propertyName: "height",
                  label: "Height",
                  size: "small",
                  description: "Set the height (e.g., 'auto', '300px'). Leave empty for auto height.",
                  placeholder: "auto",
                  jsSetting: true,
                },
              ],
            })
            .addSettingsInput({
              inputType: "textField",
              propertyName: "downloadButtonText",
              label: "Download Button Text",
              size: "small",
              description: "Custom text for the download button",
              placeholder: "Download Recording",
              jsSetting: true,
            })
            .addSettingsInput({
              inputType: "textField",
              propertyName: "loadingText",
              label: "Loading Text",
              size: "small",
              description: "Custom text to display while loading the recording",
              placeholder: "Loading recording...",
              jsSetting: true,
            })
            .addSettingsInput({
              inputType: "textField",
              propertyName: "noRecordingText",
              label: "No Recording Text",
              size: "small",
              description: "Custom message when no recording ID is provided",
              placeholder: "Please provide a recording ID to play or download the recording.",
              jsSetting: true,
            })
            .toJson(),
        },
      ],
    })
    .toJson(),
  formSettings: {
    colon: false,
    layout: "vertical",
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  },
});
